import { useState, useEffect, useCallback } from 'react';
import { MessageSquareQuote } from 'lucide-react';

interface TextSelectionTooltipProps {
  containerRef: React.RefObject<HTMLDivElement>;
  onAskAboutSelection: (selectedText: string) => void;
}

export function TextSelectionTooltip({ containerRef, onAskAboutSelection }: TextSelectionTooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [selectedText, setSelectedText] = useState('');

  const handleSelection = useCallback(() => {
    const selection = window.getSelection();
    const text = selection?.toString().trim();

    if (!text || text.length < 3) {
      setIsVisible(false);
      setSelectedText('');
      return;
    }

    // Check if selection is within the container
    if (containerRef.current && selection?.rangeCount) {
      const range = selection.getRangeAt(0);
      const container = containerRef.current;
      
      if (!container.contains(range.commonAncestorContainer)) {
        setIsVisible(false);
        return;
      }

      const rect = range.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Position the tooltip above the selection
      setPosition({
        top: rect.top - containerRect.top - 45,
        left: rect.left - containerRect.left + rect.width / 2,
      });

      setSelectedText(text);
      setIsVisible(true);
    }
  }, [containerRef]);

  const handleClick = () => {
    if (selectedText) {
      onAskAboutSelection(selectedText);
      setIsVisible(false);
      // Clear the selection
      window.getSelection()?.removeAllRanges();
    }
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleSelection);
    document.addEventListener('keyup', handleSelection);

    return () => {
      document.removeEventListener('mouseup', handleSelection);
      document.removeEventListener('keyup', handleSelection);
    };
  }, [handleSelection]);

  // Hide tooltip when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-selection-tooltip]')) {
        // Small delay to allow the click handler to fire first
        setTimeout(() => {
          const selection = window.getSelection();
          if (!selection?.toString().trim()) {
            setIsVisible(false);
          }
        }, 100);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isVisible) return null;

  return (
    <div
      data-selection-tooltip
      className="absolute z-50 animate-fade-in"
      style={{
        top: position.top,
        left: position.left,
        transform: 'translateX(-50%)',
      }}
    >
      <button
        onClick={handleClick}
        className="flex items-center gap-2 px-3 py-2 bg-[#002B5B] text-white text-sm font-medium rounded-lg shadow-lg hover:bg-[#002B5B]/90 transition-colors whitespace-nowrap"
      >
        <MessageSquareQuote className="h-4 w-4" />
        Preguntar sobre esto
      </button>
      {/* Arrow pointing down */}
      <div 
        className="absolute left-1/2 -bottom-2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-t-[8px] border-l-transparent border-r-transparent border-t-[#002B5B]"
      />
    </div>
  );
}
